import { useCallback, useEffect, useState } from "react";
import { ApiError, type PublicCampaignView } from "@keydials/api-client";
import { isPlausibleEmail } from "@keydials/ui";
import { DiscountCodeField } from "../components/DiscountCodeField";
import { TermsAcceptance, type TermsState } from "../components/TermsAcceptance";
import { api } from "../auth/AuthContext";
import { includesLabel, priceLabel } from "./BuyPlans";
import { currentRef } from "../referralCode";

/**
 * What somebody who scanned a rep's QR code lands on (KEY-432) — the fourth sessionless front door,
 * alongside the guest invite, the enquiry form and the buy page.
 *
 * <b>One offer, not a price list.</b> The whole point of a campaign is that a person standing in front of
 * a stand was told about one thing; showing them the full ladder here would undo that conversation. The
 * tier comes from the campaign and the server takes it from there too — a posted plan id is ignored,
 * because the key is printed on a poster and anybody who scans it can post whatever they like alongside it.
 *
 * <b>A dead key says so.</b> An unknown, deactivated or retired-tier link renders an honest dead end
 * rather than falling back to the ordinary buy page. Being quietly moved onto a different offer while the
 * rep who gave you the code is watching is worse than being told the link has expired.
 */
export function CampaignJoin() {
  const key = window.location.pathname.split("/")[2] ?? "";

  const [campaign, setCampaign] = useState<PublicCampaignView | null>(null);
  const [deadEnd, setDeadEnd] = useState(false);
  const [email, setEmail] = useState("");
  const [optIn, setOptIn] = useState(false);
  /** The End User Terms version accepted with both boxes ticked (KEY-618); null blocks the sign-up. */
  const [acceptedVersion, setAcceptedVersion] = useState<string | null>(null);
  const [termsReload, setTermsReload] = useState(0);
  const onTerms = useCallback((state: TermsState) => setAcceptedVersion(state.acceptedVersion), []);
  /** A discount code (KEY-642) — checked by the server against the CAMPAIGN's tier. */
  const [discountCode, setDiscountCode] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .getPublicCampaign(key)
      .then((view) => {
        if (!cancelled) setCampaign(view);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        // A 404 is the designed answer for a link that is no longer live; anything else is a blip, and
        // the same screen is the honest thing to show for both — we cannot offer this sign-up either way.
        if (!(e instanceof ApiError) || e.status === 404) setDeadEnd(true);
        else setError(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, [key]);

  const join = async () => {
    if (!campaign) return;
    setBusy(true);
    setError(null);
    try {
      const { url } = await api.startPublicCheckout({
        // Sent for completeness; the server takes the tier from the campaign key regardless.
        planId: campaign.plan.id,
        email: email.trim(),
        campaignKey: key,
        marketingOptIn: optIn,
        termsVersion: acceptedVersion,
        ageConfirmed: acceptedVersion !== null,
        discountCode,
        // An affiliate's referral remembered from an earlier link (KEY-514); the server ignores a dead one.
        affiliateCode: currentRef(window.location.search),
      });
      // Stripe's hosted page takes it from here — card details never touch this app.
      window.location.assign(url);
    } catch (e) {
      const message = e instanceof Error ? e.message : "Couldn't start your sign-up.";
      if (/updated/i.test(message)) setTermsReload((n) => n + 1);
      setError(message);
      setBusy(false);
    }
  };

  if (deadEnd) {
    return (
      <main className="buy-page">
        <header className="buy-header">
          <h1>This sign-up link has expired</h1>
          <p className="buy-muted">
            The offer behind this code is no longer running. Have a look at{" "}
            <a href="/billing/plans">our current plans</a>, or ask whoever gave you the code for a new one.
          </p>
        </header>
      </main>
    );
  }

  if (!campaign) {
    return (
      <main className="buy-page">
        <p className="buy-muted">Loading…</p>
      </main>
    );
  }

  const plan = campaign.plan;
  const trial = plan.trialDays > 0;

  return (
    <main
      className="buy-page"
      // The rep's own accent, where they set one. Scoped to this page rather than the app theme.
      style={campaign.accentColour ? ({ "--accent": campaign.accentColour } as React.CSSProperties) : undefined}
    >
      <header className="buy-header">
        {campaign.logoUrl && <img className="join-logo" src={campaign.logoUrl} alt="" />}
        <h1>{campaign.displayName}</h1>
        {campaign.introText && <p className="buy-muted">{campaign.introText}</p>}
      </header>

      <section className="buy-family">
        <div className="buy-tiers">
          {/* Deliberately not a button: there is nothing to choose between. */}
          <div className="buy-tier is-chosen">
            <span className="buy-tier-name">
              {plan.familyLabel} {plan.displayName ?? plan.tierName}
            </span>
            <span className="buy-tier-price">{priceLabel(plan)}</span>
            <span className="buy-tier-vat">{plan.taxInclusive ? "including VAT" : "plus VAT"}</span>
            <span className="buy-tier-includes">
              {includesLabel(plan)}
            </span>
            {trial && (
              <span className="buy-tier-trial">
                {plan.cardFreeTrial
                  ? `${plan.trialDays}-day free trial — no card needed`
                  : `${plan.trialDays}-day free trial`}
              </span>
            )}
          </div>
        </div>
      </section>

      <TermsAcceptance
        period={plan.period === "Annual" ? "Annual" : "Monthly"}
        cardFreeTrial={plan.cardFreeTrial && trial}
        disabled={busy}
        reloadKey={termsReload}
        onChange={onTerms}
      />

      <section className="buy-checkout">
        <label htmlFor="join-email">Your email address</label>
        <input
          id="join-email"
          type="email"
          autoComplete="email"
          value={email}
          disabled={busy}
          placeholder="you@yourcompany.co.uk"
          onChange={(e) => setEmail(e.target.value)}
        />
        <p className="buy-muted">
          This is how you'll sign in. We'll email you a code each time — there's no password to remember.
        </p>

        {/* Unticked by default, and it stays that way unless they tick it. Consent is theirs to give. */}
        <label className="join-optin">
          <input type="checkbox" checked={optIn} disabled={busy} onChange={(e) => setOptIn(e.target.checked)} />
          <span>Email me occasionally about Key Dials. You can unsubscribe at any time.</span>
        </label>

        <DiscountCodeField planId={plan.id} period={plan.period} fullPricePence={plan.amountPence} disabled={busy} onChange={setDiscountCode} />

        {error && <p className="buy-error">{error}</p>}

        <button className="buy-go" type="button" disabled={busy || !isPlausibleEmail(email) || !acceptedVersion} onClick={join}>
          {busy
            ? "Taking you to checkout…"
            : trial
              ? `Start your ${plan.trialDays}-day free trial`
              : "Continue to payment"}
        </button>

        {trial && (
          <p className="buy-muted">
            {plan.cardFreeTrial
              ? "No card needed and nothing is charged. When the trial ends it simply stops — your dials are kept, and subscribing brings them straight back."
              : "Your card is taken now and charged when the trial ends. Cancel before then and you pay nothing."}
          </p>
        )}
      </section>
    </main>
  );
}
